import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from './ThemedText';

const InputContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.theme.typography.weights.semiBold};
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.sizes.default};
  line-height: ${props => props.theme.typography.lineHeights.default};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;
  min-height: 40px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }

  &:disabled {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }

  ${props => props.error && `
    border-color: ${props.theme.colors.danger};
    &:focus {
      border-color: ${props.theme.colors.danger};
      box-shadow: 0 0 0 2px ${props.theme.colors.danger}20;
    }
  `}

  ${props => props.$dynamicBg && `background-color: ${props.$dynamicBg};`}
  ${props => props.$dynamicFg && `
    color: ${props.$dynamicFg};
    &::placeholder { color: ${props.$dynamicFg}; opacity: 0.65; }
  `}
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.sizes.default};
  line-height: ${props => props.theme.typography.lineHeights.default};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: ${props => props.rows * 24}px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }

  &:disabled {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }

  ${props => props.error && `
    border-color: ${props.theme.colors.danger};
    &:focus {
      border-color: ${props.theme.colors.danger};
      box-shadow: 0 0 0 2px ${props.theme.colors.danger}20;
    }
  `}

  ${props => props.$dynamicBg && `background-color: ${props.$dynamicBg};`}
  ${props => props.$dynamicFg && `
    color: ${props.$dynamicFg};
    &::placeholder { color: ${props.$dynamicFg}; opacity: 0.65; }
  `}
`;

const ErrorMessage = styled.div`
  margin-top: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.danger};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const RequiredIndicator = styled.span`
  color: ${props => props.theme.colors.danger};
  margin-left: 2px;
`;

export const Input = ({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  error,
  rows = 3,
  className,
  dynamicColor,
  ...props
}) => {
  const theme = useTheme();

  const InputComponent = type === 'textarea' ? StyledTextArea : StyledInput;

  return (
    <InputContainer theme={theme} className={className}>
      {label && (
        <Label theme={theme}>
          {label}
          {required && <RequiredIndicator theme={theme}>*</RequiredIndicator>}
        </Label>
      )}
      <InputComponent
        theme={theme}
        type={type === 'textarea' ? undefined : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        error={error}
        rows={type === 'textarea' ? rows : undefined}
        $dynamicBg={dynamicColor?.bg}
        $dynamicFg={dynamicColor?.fg}
        {...props}
      />
      {error && (
        <ErrorMessage theme={theme}>
          {error}
        </ErrorMessage>
      )}
    </InputContainer>
  );
};